/**
 * Product page interactions.
 * Handles color swatch, size swatch selection, variant switching,
 * and size guide popup.
 */
(function () {
  const variantsEl = document.querySelector("[data-product-variants]");
  if (!variantsEl) return;

  let variants;
  try {
    variants = JSON.parse(variantsEl.textContent);
  } catch {
    return;
  }

  const state = {
    color: null,
    size: null,
    // Options that are neither color nor size, keyed by option index.
    options: {},
  };

  const stickyNav = document.querySelector("[data-pdp-sticky-nav]");
  const stickySubmitButtons = document.querySelectorAll("[data-sticky-submit]");
  const stickySizeButtons = document.querySelectorAll("[data-sticky-size]");
  const stickyColorButtons = document.querySelectorAll("[data-sticky-color]");
  const stickySizeSelects = document.querySelectorAll(
    "[data-sticky-size-select]",
  );
  const stickyColorSelects = document.querySelectorAll(
    "[data-sticky-color-select]",
  );
  const bisNotifyWrappers = document.querySelectorAll("[data-bis-notify]");
  const priceContainers = document.querySelectorAll("[data-product-price]");

  // Initialize from current selections
  const activeColor = document.querySelector("[data-color-swatch].is-active");
  if (activeColor) {
    state.color = activeColor.dataset.color;
  } else {
    const selectedColorLabel = document.querySelector("[data-selected-color]");
    const selectedColorText = selectedColorLabel?.textContent?.trim();
    if (selectedColorText) state.color = selectedColorText;
  }

  const activeSize = document.querySelector("[data-size-swatch].is-active");
  if (activeSize) state.size = activeSize.dataset.size;

  document
    .querySelectorAll("[data-option-swatch].is-active")
    .forEach((swatch) => {
      state.options[swatch.dataset.optionIndex] = swatch.dataset.optionValue;
    });

  // ── Color swatches ──
  document.querySelectorAll("[data-color-swatch]").forEach((swatch) => {
    swatch.addEventListener("click", () => {
      state.color = swatch.dataset.color;

      // Update active states across all product-info containers
      document
        .querySelectorAll("[data-color-swatch]")
        .forEach((s) => s.classList.remove("is-active"));
      document
        .querySelectorAll(`[data-color-swatch][data-color="${state.color}"]`)
        .forEach((s) => s.classList.add("is-active"));

      // Update color label
      document.querySelectorAll("[data-selected-color]").forEach((label) => {
        label.textContent = state.color;
      });

      updateVariant();
    });
  });

  // ── Size swatches ──
  document.querySelectorAll("[data-size-swatch]").forEach((swatch) => {
    swatch.addEventListener("click", () => {
      state.size = swatch.dataset.size;

      // Update active states
      document.querySelectorAll("[data-size-swatch]").forEach((s) => {
        s.classList.remove("is-active");
        const underline = s.querySelector("[data-size-underline]");
        if (underline) {
          underline.classList.remove("opacity-100");
          underline.classList.add("opacity-0");
        }
      });
      document
        .querySelectorAll(`[data-size-swatch][data-size="${state.size}"]`)
        .forEach((s) => {
          s.classList.add("is-active");
          const underline = s.querySelector("[data-size-underline]");
          if (underline) {
            underline.classList.remove("opacity-0");
            underline.classList.add("opacity-100");
          }
        });

      updateVariant();
    });
  });

  // ── Other option swatches (options that aren't color or size) ──
  document.querySelectorAll("[data-option-swatch]").forEach((swatch) => {
    swatch.addEventListener("click", () => {
      const index = swatch.dataset.optionIndex;
      const value = swatch.dataset.optionValue;
      state.options[index] = value;

      // Update active states across all product-info containers (desktop + mobile)
      document
        .querySelectorAll(`[data-option-swatch][data-option-index="${index}"]`)
        .forEach((s) => {
          const isActive = s.dataset.optionValue === value;
          s.classList.toggle("is-active", isActive);
          const underline = s.querySelector("[data-option-underline]");
          if (underline) {
            underline.classList.toggle("opacity-100", isActive);
            underline.classList.toggle("opacity-0", !isActive);
          }
        });

      updateVariant();
    });
  });

  function getBisEmailFromConfig() {
    const helperEmail =
      window._BISConfig?.helper?.customer_email ||
      window._BISConfig?.helper?.email ||
      window._BISConfig?.customer?.email ||
      window._BISConfig?.theme?.notify?.customerEmail;

    if (typeof helperEmail === "string" && helperEmail.trim().length > 0) {
      return helperEmail.trim();
    }

    const helperNode = document.querySelector("[data-bis-helper]");
    if (!helperNode) return "";

    try {
      const parsed = JSON.parse(helperNode.textContent || "{}");
      const parsedEmail = parsed?.customerEmail;
      return typeof parsedEmail === "string" ? parsedEmail : "";
    } catch {
      return "";
    }
  }

  function setBisMessage(wrapper, message, isError) {
    const messageEl = wrapper.querySelector("[data-bis-message]");
    if (!messageEl) return;

    messageEl.textContent = message;
    messageEl.classList.remove("hidden", "text-[#5fd49e]", "text-[#ff8d8d]");
    messageEl.classList.add(isError ? "text-[#ff8d8d]" : "text-[#5fd49e]");
  }

  function resetBisMessage(wrapper) {
    const messageEl = wrapper.querySelector("[data-bis-message]");
    if (!messageEl) return;
    messageEl.textContent = "";
    messageEl.classList.add("hidden");
    messageEl.classList.remove("text-[#5fd49e]", "text-[#ff8d8d]");
  }

  function openBisModalWithAvailableAdapters(payload) {
    const adapters = [
      window.BIS,
      window.BackInStock,
      window._BISConfig?.helper,
      window._BISConfig,
    ];

    const methodNames = [
      "open",
      "openModal",
      "show",
      "showModal",
      "trigger",
      "launch",
      "notify",
      "notifyMe",
    ];

    for (const adapter of adapters) {
      if (!adapter) continue;
      for (const methodName of methodNames) {
        const method = adapter[methodName];
        if (typeof method === "function") {
          method.call(adapter, payload);
          return true;
        }
      }
    }

    const trigger = document.querySelector(
      "#BIS_trigger, .BIS_trigger, [data-bis-trigger], [data-bis-open]",
    );
    if (trigger instanceof HTMLElement) {
      trigger.dataset.variantId = String(
        payload.variant_id || payload.variantId || "",
      );
      trigger.dataset.productId = String(
        payload.product_id || payload.productId || "",
      );
      trigger.click();
      return true;
    }

    const frame = document.getElementById("BIS_frame");
    if (frame instanceof HTMLIFrameElement) {
      frame.style.display = "block";
      frame.contentWindow?.postMessage(
        {
          type: "BIS_OPEN",
          payload,
        },
        "*",
      );
      return true;
    }

    return false;
  }

  async function submitBisWithAvailableAdapters(payload) {
    const adapters = [
      window.BIS,
      window.BackInStock,
      window._BISConfig?.helper,
      window._BISConfig,
    ];

    const methodNames = [
      "subscribe",
      "subscribeToBackInStock",
      "submitBackInStock",
      "createSubscription",
    ];

    for (const adapter of adapters) {
      if (!adapter) continue;
      for (const methodName of methodNames) {
        const method = adapter[methodName];
        if (typeof method === "function") {
          return method.call(adapter, payload);
        }
      }
    }

    const subscribeUrl =
      window._BISConfig?.endpoints?.subscribe ||
      window._BISConfig?.subscribe_url ||
      window._BISConfig?.subscribeUrl ||
      window._BISConfig?.urls?.subscribe;

    if (typeof subscribeUrl === "string" && subscribeUrl.length > 0) {
      const response = await fetch(subscribeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok || data?.success === false) {
        throw new Error(
          data?.message || `Back in stock request failed: ${response.status}`,
        );
      }

      return data;
    }

    throw new Error(
      "No Back in Stock adapter was found. Verify the app embed is enabled.",
    );
  }

  function updateNotifyForms(variant) {
    if (!variant || bisNotifyWrappers.length === 0) return;

    bisNotifyWrappers.forEach((wrapper) => {
      const shouldShow = wrapper.dataset.bisProductUnavailable === "true";
      wrapper.classList.toggle("hidden", !shouldShow);
      wrapper.setAttribute("aria-hidden", shouldShow ? "false" : "true");
      wrapper.dataset.bisVariantId = String(variant.id);

      wrapper
        .querySelectorAll("[data-bis-variant-input]")
        .forEach((input) => (input.value = String(variant.id)));
    });
  }

  function initBisForms() {
    if (bisNotifyWrappers.length === 0) return;

    bisNotifyWrappers.forEach((wrapper) => {
      const form = wrapper.querySelector("[data-bis-form]");
      const submitButton = wrapper.querySelector("[data-bis-submit]");
      const emailInput = wrapper.querySelector("[data-bis-email]");
      const variantInput = wrapper.querySelector("[data-bis-variant-input]");
      const productInput = wrapper.querySelector("[data-bis-product-input]");

      const openButton = wrapper.querySelector("[data-bis-open-modal]");
      if (
        !form ||
        !submitButton ||
        !emailInput ||
        !variantInput ||
        !productInput
      ) {
        if (!openButton || !variantInput || !productInput) return;

        openButton.addEventListener("click", () => {
          const payload = {
            product_id: Number(productInput.value),
            variant_id: Number(variantInput.value),
            productId: Number(productInput.value),
            variantId: Number(variantInput.value),
          };

          const opened = openBisModalWithAvailableAdapters(payload);
          if (!opened) {
            console.warn(
              "Back in stock modal adapter was not found. Verify the app embed is enabled.",
            );
          }
        });

        return;
      }

      if (!emailInput.value) {
        const configEmail = getBisEmailFromConfig();
        if (configEmail) emailInput.value = configEmail;
      }

      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        resetBisMessage(wrapper);

        const email = (emailInput.value || "").trim();
        if (!email) {
          setBisMessage(
            wrapper,
            wrapper.dataset.bisErrorMessage || "Please enter your email.",
            true,
          );
          emailInput.focus();
          return;
        }

        const originalLabel =
          submitButton.dataset.bisSubmitLabel ||
          submitButton.textContent?.trim() ||
          "Notify Me";
        const loadingLabel =
          submitButton.dataset.bisSubmitLoadingLabel || "Sending...";
        submitButton.disabled = true;
        submitButton.textContent = loadingLabel;

        const payload = {
          email,
          product_id: Number(productInput.value),
          variant_id: Number(variantInput.value),
          productId: Number(productInput.value),
          variantId: Number(variantInput.value),
        };

        try {
          document.dispatchEvent(
            new CustomEvent("bis:notify-submit", {
              detail: {
                payload,
                wrapper,
              },
            }),
          );

          await submitBisWithAvailableAdapters(payload);
          setBisMessage(
            wrapper,
            wrapper.dataset.bisSuccessMessage ||
              "Thanks! We'll email you when this item is back in stock.",
            false,
          );
        } catch (error) {
          console.error("Back in stock subscribe error:", error);
          setBisMessage(
            wrapper,
            wrapper.dataset.bisErrorMessage ||
              "Unable to subscribe right now. Please try again.",
            true,
          );
        } finally {
          submitButton.disabled = false;
          submitButton.textContent = originalLabel;
        }
      });
    });
  }

  function updateVariant() {
    const variant = variants.find((v) => {
      let match = true;
      if (state.color) {
        const colorIdx = getOptionIndex("color");
        if (colorIdx !== -1)
          match = match && v.options[colorIdx] === state.color;
      }
      if (state.size) {
        const sizeIdx = getOptionIndex("size");
        if (sizeIdx !== -1) match = match && v.options[sizeIdx] === state.size;
      }
      Object.keys(state.options).forEach((index) => {
        match = match && v.options[Number(index)] === state.options[index];
      });
      return match;
    });

    if (!variant) return;

    syncVariantPricing(variant);
    syncAfterpayPlacement(variant);

    // Update hidden variant input
    document.querySelectorAll("[data-variant-input]").forEach((input) => {
      input.value = variant.id;
    });

    // Update URL without reload
    const url = new URL(window.location);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState({}, "", url);

    // Update add-to-cart button. The label comes from the button itself so
    // pre-order products (custom.preorder metafield) keep their "Pre-Order" text.
    document.querySelectorAll("[data-main-atc-submit]").forEach((btn) => {
      btn.disabled = !variant.available;
      btn.textContent = btn.dataset.atcLabel || "Add to Cart";
    });

    document.querySelectorAll("[data-main-atc-row]").forEach((row) => {
      const shouldShow = Boolean(variant.available);
      row.classList.toggle("hidden", !shouldShow);
      row.setAttribute("aria-hidden", shouldShow ? "false" : "true");
    });

    // Show our branded "Notify me when available" button (a .BIS_trigger the
    // Back-in-Stock app binds to) when the selected variant is sold out.
    document.querySelectorAll("[data-bis-row]").forEach((row) => {
      const showNotify = !variant.available;
      row.classList.toggle("hidden", !showNotify);
      row.querySelectorAll("[data-bis-trigger]").forEach((trigger) => {
        trigger.dataset.bisVariantId = String(variant.id);
      });
    });

    // Pin the image of the colour that is actually selected.
    const variantImage = variant.featured_image?.src;
    if (variantImage) {
      const absolute = variantImage.startsWith("//")
        ? "https:" + variantImage
        : variantImage;
      document.querySelectorAll("[data-product-share]").forEach((row) => {
        row.dataset.shareImage = absolute;
      });
    }

    syncLowStockMessage(variant);
    syncStickyControls(variant);
    updateNotifyForms(variant);
  }

  function formatMoneyFromContainer(cents, container) {
    const fallback = `$${(Number(cents || 0) / 100).toFixed(2)}`;
    const format = container?.dataset?.moneyFormat;

    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents, format || "${{amount}}");
    }

    return fallback;
  }

  function syncVariantPricing(variant) {
    if (!variant || priceContainers.length === 0) return;

    priceContainers.forEach((container) => {
      const priceHtml = formatMoneyFromContainer(variant.price, container);
      const compareAt = Number(variant.compare_at_price || 0);
      const showCompareAt = compareAt > Number(variant.price || 0);

      if (showCompareAt) {
        const compareHtml = formatMoneyFromContainer(compareAt, container);
        container.innerHTML = `${priceHtml}<span class="text-white/50 line-through ml-2" data-product-compare-price>${compareHtml}</span>`;
        return;
      }

      container.innerHTML = priceHtml;
    });
  }

  function syncAfterpayPlacement(variant) {
    const placements = document.querySelectorAll("[data-afterpay-placement]");

    if (!variant || placements.length === 0) return;

    const amount = (Number(variant.price || 0) / 100).toFixed(2);

    placements.forEach((placement) => {
      placement.setAttribute("data-amount", amount);
      placement.setAttribute("data-variant-id", String(variant.id));
    });

    const ap = window.AfterPay || window.Afterpay || window.afterpay;
    if (ap && typeof ap.refresh === "function") {
      ap.refresh();
      return;
    }

    // If the API object is not available, remounting prompts the custom element to re-render.
    placements.forEach((placement) => {
      const clone = placement.cloneNode(true);
      placement.replaceWith(clone);
    });
  }

  function syncLowStockMessage(variant) {
    const stockMessages = document.querySelectorAll("[data-low-stock-message]");
    if (stockMessages.length === 0) return;

    stockMessages.forEach((message) => {
      const fixedEnabled = message.dataset.lowStockFixedEnabled === "true";
      const fixedValue = Number(message.dataset.lowStockFixedValue || 0);
      const threshold = Number(message.dataset.lowStockThreshold || 0);

      let shouldShow = false;
      let displayQty = fixedValue;

      if (fixedEnabled) {
        shouldShow = true;
      } else {
        const inventoryQty = Number(variant?.inventory_quantity ?? 0);
        const inventoryManaged = Boolean(variant?.inventory_management);

        shouldShow =
          Boolean(variant?.available) &&
          inventoryManaged &&
          inventoryQty > 0 &&
          inventoryQty <= threshold;
        displayQty = inventoryQty;
      }

      const countEl = message.querySelector("[data-low-stock-count]");
      if (countEl) countEl.textContent = String(displayQty);
      message.classList.toggle("hidden", !shouldShow);
    });
  }

  function getOptionIndex(name) {
    if (variants.length === 0) return -1;
    const nameLower = name.toLowerCase();
    const colorSwatch = document.querySelector("[data-color-swatch]");
    const sizeSwatch = document.querySelector("[data-size-swatch]");

    let probeValue = nameLower === "color" ? state.color : state.size;

    if (!probeValue && nameLower === "color" && colorSwatch) {
      probeValue = colorSwatch.dataset.color;
    }

    if (!probeValue && nameLower === "size" && sizeSwatch) {
      probeValue = sizeSwatch.dataset.size;
    }

    if (!probeValue && nameLower === "color") {
      const selectedColorLabel = document.querySelector(
        "[data-selected-color]",
      );
      const selectedColorText = selectedColorLabel?.textContent?.trim();
      if (selectedColorText) probeValue = selectedColorText;
    }

    if (probeValue) {
      for (let i = 0; i < 3; i++) {
        if (variants.some((vr) => vr.options[i] === probeValue)) return i;
      }
    }

    if (nameLower === "color" && colorSwatch) {
      const colorVal = colorSwatch.dataset.color;
      for (let i = 0; i < 3; i++) {
        if (variants.some((vr) => vr.options[i] === colorVal)) return i;
      }
    }
    if (nameLower === "size" && sizeSwatch) {
      const sizeVal = sizeSwatch.dataset.size;
      for (let i = 0; i < 3; i++) {
        if (variants.some((vr) => vr.options[i] === sizeVal)) return i;
      }
    }
    return -1;
  }

  function getActiveProductForm() {
    const forms = Array.from(
      document.querySelectorAll("[data-product-info] product-form"),
    );
    return forms.find((form) => form.offsetParent !== null) || forms[0] || null;
  }

  function syncStickyControls(variant) {
    if (!stickyNav) return;

    const colorIdx = getOptionIndex("color");
    const sizeIdx = getOptionIndex("size");

    const selectedColor = colorIdx !== -1 ? variant.options[colorIdx] : null;
    const selectedSize = sizeIdx !== -1 ? variant.options[sizeIdx] : null;

    stickyColorButtons.forEach((btn) => {
      btn.classList.toggle("is-active", btn.dataset.color === selectedColor);
    });

    stickySizeButtons.forEach((btn) => {
      btn.classList.toggle("is-active", btn.dataset.size === selectedSize);
    });

    stickyColorSelects.forEach((select) => {
      if (selectedColor) select.value = selectedColor;
    });

    stickySizeSelects.forEach((select) => {
      if (selectedSize) select.value = selectedSize;
    });

    stickySubmitButtons.forEach((btn) => {
      btn.disabled = !variant.available;
      btn.textContent = btn.dataset.atcLabel || "Add to Cart";
      btn.classList.toggle("hidden", !variant.available);
    });
  }

  function initStickyNav() {
    if (!stickyNav) return;

    const triggerRows = Array.from(
      document.querySelectorAll("[data-main-atc-row]"),
    );
    if (triggerRows.length === 0) return;

    const stickyClass = "is-visible";
    const setStickyVisible = (visible) => {
      stickyNav.classList.toggle(stickyClass, visible);
      stickyNav.setAttribute("aria-hidden", visible ? "false" : "true");
    };

    // Keep sticky hidden at initial page load until user scrolls past ATC row.
    setStickyVisible(false);

    const pickVisibleTrigger = () =>
      triggerRows.find((row) => row.offsetParent !== null) || triggerRows[0];

    const observeTrigger = () => {
      const activeTrigger = pickVisibleTrigger();
      if (!activeTrigger) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          // Show only when the add-to-cart row has actually scrolled past the top edge.
          // This prevents sticky from appearing on initial load when ATC is below the fold.
          const shouldShow = entry.boundingClientRect.bottom <= 0;
          setStickyVisible(shouldShow);
        },
        {
          threshold: 0,
          rootMargin: "0px",
        },
      );

      observer.observe(activeTrigger);
      return observer;
    };

    let observer = observeTrigger();

    window.addEventListener("resize", () => {
      observer?.disconnect();
      observer = observeTrigger();
    });

    stickySizeButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        state.size = btn.dataset.size;
        updateVariant();
      });
    });

    stickyColorButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.disabled) return;
        state.color = btn.dataset.color;
        updateVariant();
      });
    });

    stickySizeSelects.forEach((select) => {
      select.addEventListener("change", () => {
        state.size = select.value;
        updateVariant();
      });
    });

    stickyColorSelects.forEach((select) => {
      select.addEventListener("change", () => {
        const selectedOption = select.options[select.selectedIndex];
        const stickyUrl = selectedOption?.dataset?.stickyUrl;
        if (stickyUrl) {
          window.location.href = stickyUrl;
          return;
        }
        state.color = select.value;
        updateVariant();
      });
    });

    stickySubmitButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const formEl = getActiveProductForm()?.querySelector("form");
        if (!formEl) return;
        if (typeof formEl.requestSubmit === "function") {
          formEl.requestSubmit();
        } else {
          formEl.dispatchEvent(
            new Event("submit", { bubbles: true, cancelable: true }),
          );
        }
      });
    });

    const initialVariant = variants.find(
      (v) =>
        Number(v.id) ===
        Number(document.querySelector("[data-variant-input]")?.value),
    );
    if (initialVariant) {
      syncStickyControls(initialVariant);
      syncLowStockMessage(initialVariant);
      updateNotifyForms(initialVariant);
    }
  }

  // ── Size guide popup ──
  function initSizeGuide() {
    const toggles = document.querySelectorAll("[data-size-guide-toggle]");
    if (toggles.length === 0) return;

    const modals = document.querySelectorAll("[data-size-guide-modal]");
    const hideAllModals = () => {
      modals.forEach((modal) => {
        modal.style.display = "none";
      });
      document.body.style.overflow = "";
    };

    toggles.forEach((btn) => {
      btn.addEventListener("click", () => {
        if (
          window.KiwiSizing &&
          typeof window.KiwiSizing.openSizeChart === "function"
        ) {
          window.KiwiSizing.openSizeChart();
          return;
        }

        const container = btn.closest("[data-product-info]");
        const modal =
          container?.querySelector("[data-size-guide-modal]") || modals[0];
        if (!modal) return;

        hideAllModals();
        modal.style.display = "flex";
        document.body.style.overflow = "hidden";
      });
    });

    modals.forEach((modal) => {
      const close = () => {
        modal.style.display = "none";
        const hasOpenModal = Array.from(modals).some(
          (m) => m.style.display === "flex",
        );
        if (!hasOpenModal) document.body.style.overflow = "";
      };

      modal
        .querySelector("[data-size-guide-close]")
        ?.addEventListener("click", close);
      modal
        .querySelector("[data-size-guide-backdrop]")
        ?.addEventListener("click", close);
    });

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      hideAllModals();
    });
  }

  // ── Share buttons ──
  function initProductShare() {
    const rows = document.querySelectorAll("[data-product-share]");
    if (!rows.length) return;

    // Always share the shop's public product URL, never window.location: on a
    // theme preview or the local dev server the address bar holds a host the
    // social crawlers cannot reach, which is why Facebook would open an empty
    // composer. The selected variant is carried over from the address bar,
    // where updateVariant() keeps it in sync.
    function shareUrl(row) {
      const base = row.dataset.shareBase;
      if (!base) return window.location.href;

      const variant = new URLSearchParams(window.location.search).get("variant");
      return variant ? `${base}?variant=${encodeURIComponent(variant)}` : base;
    }

    // Pinterest needs an absolute image URL. Resolving against the shop URL
    // covers protocol-relative CDN paths ("//cdn.shopify.com/…") and anything
    // the preview host rewrites to a root-relative path.
    function shareImage(row) {
      const image = row.dataset.shareImage;
      if (!image) return "";
      try {
        return new URL(image, row.dataset.shareBase || window.location.href).href;
      } catch {
        return "";
      }
    }

    function showFeedback(row, message) {
      const el = row.querySelector("[data-share-feedback]");
      if (!el) return;
      el.textContent = message;
      el.classList.remove("opacity-0");
      clearTimeout(el._shareFeedbackTimer);
      el._shareFeedbackTimer = setTimeout(() => {
        el.classList.add("opacity-0");
      }, 2500);
    }

    async function copyLink(url) {
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
          return true;
        } catch {
          // Falls through to the textarea fallback (blocked permission, http).
        }
      }
      try {
        const field = document.createElement("textarea");
        field.value = url;
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.appendChild(field);
        field.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(field);
        return ok;
      } catch {
        return false;
      }
    }

    function openShareWindow(url) {
      window.open(url, "_blank", "noopener,noreferrer,width=600,height=650");
    }

    rows.forEach((row) => {
      row.querySelectorAll("[data-share]").forEach((btn) => {
        btn.addEventListener("click", async (event) => {
          const network = btn.dataset.share;
          const url = shareUrl(row);
          const title = row.dataset.shareTitle || document.title;
          const text = row.dataset.shareText || title;
          const image = shareImage(row);

          if (network === "facebook") {
            event.preventDefault();
            openShareWindow(
              "https://www.facebook.com/sharer/sharer.php?u=" +
                encodeURIComponent(url),
            );
            return;
          }

          if (network === "pinterest") {
            event.preventDefault();
            const params = new URLSearchParams({ url, description: text });
            if (image) params.set("media", image);
            openShareWindow(
              "https://www.pinterest.com/pin/create/button/?" + params,
            );
            return;
          }

          if (network === "copy") {
            event.preventDefault();
            const copied = await copyLink(url);
            showFeedback(row, copied ? "Link copied" : "Press Ctrl+C to copy");
            return;
          }

          if (network === "instagram") {
            // "profile" mode just follows the href to the store's profile.
            if (row.dataset.instagramMode === "profile") return;

            event.preventDefault();

            // Instagram accepts no share URL, so hand off to the OS share sheet
            // where it exists — Instagram is one of the targets there.
            if (navigator.share) {
              try {
                await navigator.share({ title, text, url });
              } catch {
                // Customer dismissed the sheet.
              }
              return;
            }

            const copied = await copyLink(url);
            showFeedback(
              row,
              copied
                ? "Link copied — paste it in your Instagram story or bio"
                : "Press Ctrl+C to copy",
            );
            const profile = row.dataset.instagramUrl;
            if (profile) window.open(profile, "_blank", "noopener");
          }
        });
      });
    });
  }

  initSizeGuide();
  initStickyNav();
  initBisForms();
  initProductShare();

  // ── Quantity dropdown ──
  document.querySelectorAll("[data-qty-wrapper]").forEach((wrapper) => {
    const toggle = wrapper.querySelector("[data-qty-toggle]");
    const dropdown = wrapper.querySelector("[data-qty-dropdown]");
    const label = wrapper.querySelector("[data-qty-label]");
    const input = wrapper.querySelector("[data-qty-input]");
    if (!toggle || !dropdown) return;

    toggle.addEventListener("click", () => {
      dropdown.classList.toggle("hidden");
    });

    wrapper.querySelectorAll("[data-qty-option]").forEach((opt) => {
      opt.addEventListener("click", () => {
        const val = opt.dataset.qtyOption;
        if (label) label.textContent = val;
        if (input) input.value = val;
        dropdown.classList.add("hidden");
      });
    });

    document.addEventListener("click", (e) => {
      if (!wrapper.contains(e.target)) {
        dropdown.classList.add("hidden");
      }
    });
  });
})();
